// `useState` / `useCallback` left with the timer machinery they served, which now lives in
// `useHoverIntent`. (`useContext` was already unused.)
import React, { useEffect, useRef } from 'react'
import { Link, useLocation, useMatch, useNavigate } from 'react-router-dom'

import { isInternalRoleName, ROLES } from 'constants/roles'

import { getNameOrEmailSlug } from 'utils'

import { useAuth, useColorMode, useIsInternalUser, useUserProfileMutations, useUserProfileQuery, useWorkOSAuth } from 'hooks'
import { useCurrentPlan, planDisplayName } from 'hooks/api'
import {
  User,
  SunMoon,
  Contrast,
  ChevronRight,
  LogOut,
  Sun,
  Moon,
  Laptop,
  Check,
} from 'lucide-react'
import type { ThemePreference } from 'types'
import { ROUTES } from 'routes'

import { Box, Stack, Typography, Avatar, Menu, MenuItem, Divider, Skeleton, Button } from '@mui/material'
import { useTheme, alpha } from '@mui/material/styles'
import { MENU_PAPER_SX, MENU_PAPER_SHADOW, MENU_ITEM_SX } from '@repo/ui'

import {
  HOVER_MENU_PAPER_SX,
  HOVER_MENU_ROOT_SX,
  useHoverIntent,
} from 'hooks/useHoverIntent'

import { accountMenuIconMotionSx, themeSubmenuIconMotionSx } from '../../../navIconMotion'
import {
  SIDEBAR_MENU_ANCHOR_ORIGIN,
  SIDEBAR_MENU_PAPER_SX_FLUSH,
  SIDEBAR_MENU_TRANSFORM_ORIGIN,
  SIDEBAR_SUBMENU_PAPER_SX,
} from '../../sidebarMenuPlacement'
import { useChromeSettledPosition } from 'hooks/useChromeSettledPosition'
import { sidebarSurface } from '../../sidebarSurface'

interface SidebarUserBlockProps {
  sidebarOpen?: boolean
}



/*
  TWO HOVER-OPENED MENUS, ONE PIECE OF MACHINERY.

  Both the account menu (off the profile chip) and its Theme submenu open on
  hover-with-intent, and the reasoning that makes that not-a-one-liner — an entry is not
  intent, a leave is not dismissal, and the modal backdrop silently breaks both — now
  lives once in `hooks/useHoverIntent`, which is also what the sidebar's Help popover
  uses. Read that file's docblock; it is the design, not a summary of it.

  The Theme submenu had its own hand-rolled copy of those timers until VRG-365 added the
  second and third consumer. Three implementations of one behaviour in two files is
  exactly the drift working-conventions §36 exists to prevent — and the copies had
  already started from different premises, since the submenu's version had no
  outside-click or Escape handling of its own (it borrowed the parent's backdrop, which
  a top-level hover menu does not have).

  What is specific to THIS file, and stays here:

  - The submenu's own delays, which are not the sidebar's. See SUBMENU_*_DELAY below.
  - Composing both hover regions onto the submenu's paper. The submenu renders in its own
    portal, so it is NOT a DOM child of the account paper — without the composition below,
    moving the pointer from the account menu into the submenu would read as leaving the
    account menu, and the parent would close 250ms later and orphan its own child.
  - The keyboard chords a submenu owes: ArrowRight opens, ArrowLeft/Escape close and
    return focus to the row.
*/

/**
 * How long the pointer must rest on Theme before the submenu opens.
 *
 * 130, NOT the sidebar's 200 (`HOVER_INTENT_OPEN_DELAY`), and the difference is the
 * geometry rather than taste: this is a 44px row inside a menu the user has already
 * committed to opening, where transit is quicker and a stray open costs less than a
 * stray open on the nav rail does. Long enough to reject the Profile -> Log out
 * pass-through that crosses this row, short enough not to read as lag.
 */
const SUBMENU_OPEN_DELAY = 130

/**
 * How long the submenu survives with the pointer on neither it nor the Theme row.
 *
 * The diagonal-travel budget, sized against the WORST journey the layout allows: Theme's
 * row centre to the "System" row, ~200px right and ~90px down. 300ms covers that at any
 * plausible pointer speed with room to spare.
 *
 * A safe-triangle (extend the live area to the corridor between the row and the paper) is
 * the other standard answer and is deliberately NOT what this does. It needs pointer
 * coordinates, both papers' rects, and a re-check loop, and it buys its accuracy back only
 * when a MIS-close would be expensive. Here the failure mode of the generous delay is that
 * the submenu lingers ~300ms after you settle on Log out, which is what every desktop menu
 * does anyway. Cheaper mechanism, same outcome.
 */
const SUBMENU_CLOSE_DELAY = 300

const getInitials = (value?: string | null) => {
  if (!value) return ''
  return value
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase() ?? '')
    .join('')
}

export const SidebarUserBlock = ({
  sidebarOpen,
}: SidebarUserBlockProps) => {
  const { profile = false, logout } = useAuth()
  // The UNMAPPED session user — the only place the real role name survives; see the
  // `roleName` note further down (VRG-410).
  const { user: workosUser } = useWorkOSAuth()
  // Avatar source of truth = the canonical admin profile (/auth/admin/profile), the
  // same source the Personal page uses, so an uploaded photo shows in the sidebar too
  // and survives a refresh. useAuth()'s WorkOS session profile only carries the
  // social/WorkOS avatar (or null) and never the locally-uploaded one.
  const { data: canonicalProfile } = useUserProfileQuery()
  const navigate = useNavigate()
  const theme = useTheme()

  const colorMode = useColorMode()
  const { updateAsync: updateProfileAsync } = useUserProfileMutations()

  /*
    The account menu. `handleTriggerClick` keeps the existing click behaviour (and adds a
    click-to-close toggle); `openedByKeyboard` replaces the old `menuAutoFocus` state.

    That state existed because the menu used to pass a flat `autoFocus={false}`, which is
    right for a mouse click (a focus ring landing on "Profile" because you clicked an
    avatar is noise) but leaves the MenuList unfocused — and MUI's arrow-key handler lives
    ON that list, so the whole menu was unreachable by keyboard. Switching it by activation
    source keeps the mouse path unchanged and makes the keyboard one work; the hook now
    derives the same fact from `event.detail === 0`.
  */
  const accountHover = useHoverIntent()
  const anchorEl = accountHover.anchorEl
  const open = accountHover.open

  /*
    The drawer's width is a 225ms transition and the hover that opens this menu is the
    hover that starts it, so without these the menu is positioned against whatever width
    the animation happened to be at. Measured 206px apart between the two outcomes — see
    `useChromeSettledPosition`.
  */
  const menuPosition = useChromeSettledPosition(open)

  /*
    The Theme submenu, on its own (faster) budget — see the constants above. Sharing the
    hook rather than the delays is the point: the machinery is universal, the numbers are
    per-geometry.
  */
  const themeHover = useHoverIntent({
    openDelay: SUBMENU_OPEN_DELAY,
    closeDelay: SUBMENU_CLOSE_DELAY,
  })
  const themeAnchorEl = themeHover.anchorEl
  const themeOpen = themeHover.open
  /* The submenu hangs off a row inside the parent paper, so it moves when the parent does. */
  const themeMenuPosition = useChromeSettledPosition(themeOpen)

  /** The Theme row itself, so ArrowLeft/Escape can hand focus back to what opened it. */
  const themeRowRef = useRef<HTMLLIElement | null>(null)

  const closeThemeMenuNow = themeHover.closeNow

  /*
    A submenu cannot outlive its parent.

    `handleClose` covers the click paths, but the account menu now has close paths that do
    not go through it — its own hover-out timer, the hook's Escape, the hook's
    outside-mousedown. And the two timers are not even the same length (250 vs 300), so a
    pointer leaving both papers would close the parent first and leave the Theme submenu
    floating over the page for 50ms with nothing under it.

    An effect catches every path at once rather than asking each one to remember.
  */
  useEffect(() => {
    if (!open) closeThemeMenuNow()
  }, [open, closeThemeMenuNow])

  /**
   * The submenu's paper belongs to BOTH hover regions.
   *
   * It renders in its own portal, so it is not a DOM descendant of the account paper and
   * the account menu's own `pointerleave` fires as the pointer crosses into it. Without
   * feeding the account region too, the parent would schedule its close and take the
   * submenu with it a beat after the user arrived.
   */
  const submenuPointerProps = {
    onPointerEnter: (event: React.PointerEvent<HTMLElement>) => {
      accountHover.surfaceProps.onPointerEnter(event)
      themeHover.surfaceProps.onPointerEnter(event)
    },
    onPointerLeave: (event: React.PointerEvent<HTMLElement>) => {
      accountHover.surfaceProps.onPointerLeave(event)
      themeHover.surfaceProps.onPointerLeave(event)
    },
  }

  /**
   * Profile and Log out. Entering a SIBLING row starts the submenu's travel budget rather
   * than closing outright — the diagonal to the submenu crosses Log out, so an immediate
   * close here would make "System" unreachable by the natural pointer path. It also
   * cancels any pending open, so sweeping through Theme on the way here cannot leave an
   * open scheduled behind you.
   */
  const handleSiblingRowEnter = () => {
    themeHover.cancelPendingOpen()
    if (themeAnchorEl) themeHover.scheduleClose()
  }

  const handleThemeClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    // Deliberately `openNow`, not the hook's toggling `handleTriggerClick`: a disclosure
    // row opens its submenu, it does not flip it shut. `detail === 0` is a click
    // synthesised from Enter/Space rather than a real pointer press — keyboard activation
    // wants focus in the submenu, a mouse click does not.
    themeHover.openNow(event.currentTarget, event.detail === 0)
  }

  /** ArrowRight opens the submenu, the conventional chord for a disclosure row. */
  const handleThemeRowKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'ArrowRight') return
    event.preventDefault()
    event.stopPropagation()
    themeHover.openNow(event.currentTarget, true)
  }

  /**
   * ArrowLeft and Escape close the SUBMENU and return to the row that opened it.
   *
   * `stopPropagation` on Escape matters: without it the parent Menu would also see it and
   * the whole account menu would close, so one keypress would undo two levels.
   */
  const handleThemeSubmenuKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'Escape') return
    event.preventDefault()
    event.stopPropagation()
    closeThemeMenuNow()
    themeRowRef.current?.focus()
  }

  const handleSelectTheme = (value: ThemePreference) => {
    colorMode.setMode(value)
    updateProfileAsync({ theme: value }).catch(() => {})
    closeThemeMenuNow()
    accountHover.closeNow()
  }

  // Closing the parent takes the submenu with it. It has to be explicit: neither menu's
  // root catches pointer events any more (`HOVER_MENU_ROOT_SX`), so nothing else would
  // dismiss an orphaned Theme submenu left floating over the page.
  const handleClose = () => {
    closeThemeMenuNow()
    // `closeAndRestoreFocus`, not `closeNow` — this is the Menu's `onClose`, the only
    // callback that fires when MUI handles Escape itself (it stops propagation, so no
    // document listener sees it). See the same note in `ActionDropdown`.
    accountHover.closeAndRestoreFocus()
  }

  // One paper treatment for all three menus here. Was copy-pasted per <Menu>, which
  // is how the Theme submenu could quietly end up with a different shadow from the
  // parent it hangs off; adding a third menu made keeping three copies in step the
  // wrong default.
  // PARITY with the Help popover, which is the reference for what a menu looks like
  // in this app. It carries the house blue-grey lift (`MENU_PAPER_SHADOW`) in both
  // modes; this menu was on `theme.shadows[3]`, MUI's neutral grey, so on white it
  // was visibly a different kind of surface from every other popover.
  //
  // Two wrong answers were tried on the way here, both worth naming. It first
  // hand-rolled `theme.shadows.default` repeated THREE times (~3x the alpha of one
  // drop shadow), which is why it read heavier than everything else. Then the shadow
  // was removed outright in light mode — which fixed "too heavy" by making it the
  // only popover with NO lift, i.e. odd in the other direction. The target was never
  // more or less elevation, it was the SAME elevation as its siblings.
  //
  // The shadow itself now lives in `@repo/ui` (§36): it was hand-rolled in four
  // separate files, which is precisely how this menu drifted off it unnoticed.
  const menuPaperSx = {
    borderRadius: 2,
    ...MENU_PAPER_SX,
    boxShadow: MENU_PAPER_SHADOW,
  }
  // The submenu clears its parent menu by the SAME offset the parent menu clears the
  // sidebar by — one constant, in `sidebarMenuPlacement`, which also carries the
  // derivation. Was a hand-tuned `ml: 7` (28px, netting ~13px); the shared value nets
  // exactly 12px, so sidebar → menu → submenu now step by one repeated gap.
  const submenuPaperSx = { ...menuPaperSx, ...SIDEBAR_SUBMENU_PAPER_SX }

  // Parity with the Personal page: derive name + initials (and the avatar) from the
  // canonical admin profile, falling back to the auth profile, then to the email's
  // first letter — so the sidebar never shows the blank user silhouette while the page
  // shows "S". (getNameOrEmailSlug can return a bare " " when first_name is "",
  // which getInitials turns into "" → silhouette; the email fallback prevents that.)
  const sourceProfile = canonicalProfile ?? (profile || undefined)
  const name = getNameOrEmailSlug(sourceProfile as any) ?? ''
  const initials =
    getInitials(name) || (sourceProfile?.email?.[0]?.toUpperCase() ?? '')

  // The secondary line is the company's PLAN, not the email — the email is already the
  // account you are signed into and repeating it under your own name told you nothing
  // you could act on, whereas the plan is the thing that gates what the app will let
  // you do.
  //
  // Internal Vera staff have no company subscription, so the query is gated off for
  // them rather than firing a doomed request on every internal route.
  // Gate on the role being KNOWN to be a company one, not merely on "not internal".
  // The role is undefined for the first render while auth resolves, so a bare
  // "not internal" test passes during that window and fires the query anyway — the plan
  // catalog was still being fetched on /internal/companies. Requiring `roleName` to be
  // present defers the decision until there is something to decide on.
  //
  // VRG-410 — the RAW WorkOS role name, and the shared `isInternalRoleName`. Two things
  // were wrong with reading `profile.role.name` here: it missed `vera_fulfillment` /
  // `vera_finance`, AND `mapWorkOSUserToProfile` maps unlisted role names to
  // `'company_team_member'`, so those two would have read as company users even after
  // being added to the list — firing the doomed plan query this gate exists to prevent.
  // Presence semantics are unchanged: `profile` is null exactly when `user` is.
  const roleName = workosUser?.role?.name
  const isCompanyUser = Boolean(roleName) && !isInternalRoleName(roleName)
  const { plan, isLoading: planLoading } = useCurrentPlan(isCompanyUser)

  /**
   * The first menu row names its DESTINATION, and the destination is not the same page
   * for both kinds of user (VRG-395).
   *
   * Both land on `/settings/profile`. For a company user that is the Profile SECTION of a
   * page titled "Settings", which they normally reach by the sidebar gear — so "Profile"
   * is the accurate name and the row reads as a deep link past the gear. For internal Vera
   * staff there is no gear and no company half: the page is titled "Account", this row is
   * its ONLY entry point, and calling it "Profile" reproduces exactly the collision that
   * got the page itself renamed away from Profile (a Profile containing a Profile).
   *
   * So the label is conditional because the PAGE TITLE is conditional, not as a flourish.
   * Renaming it for everyone would not be neutral — it would hand company users a row
   * labelled "Account" opening a page titled "Settings", i.e. the same mismatch pointing
   * the other way.
   *
   * ⚠️ `useIsInternalUser()`, NOT the `isCompanyUser` above. That one is deliberately
   * role-only (it gates the plan query and has to stay false until `roleName` resolves),
   * so it reads FALSE for a staffer inside a company's space — who sees the full,
   * "Settings"-titled page and must therefore still see "Profile". The hook carries the
   * `!inSpace` clause; the local boolean does not. Kept as a label rather than a second
   * near-identical boolean, so the two can't be mistaken for each other at a glance.
   */
  const accountRowLabel = useIsInternalUser() ? 'Account' : 'Profile'

  // Fall back to the email rather than rendering an empty line. That covers internal
  // staff (no plan by design) and a company with no resolvable subscription, and keeps
  // the block the same height in every case — this row is sticky and fixed-height, so a
  // missing second line would shift the name off centre.
  // No strip here any more: `subscriptionApi` removes the "Vera " prefix at the
  // boundary, so every surface reads the same name. Stripping again at one render site
  // is what produced the split where this chip said "Premium Plan" and Settings →
  // Billing said "Vera Premium Plan".
  const secondaryLine = plan?.title ?? sourceProfile?.email

  return (
    <>
      {/* <Button onClick={() => colorMode.setLightMode()}>Light Mode</Button>
      <Button onClick={() => colorMode.setDarkMode()}>Dark Mode</Button> */}
      {/* A real BUTTON, not a div with an onClick.
          It sits directly under the reader row, which IS a ListItemButton — so the
          reader highlighted on hover and this never did, even though the two read as
          the same kind of row. Worse, a click-handler on a Box is mouse-only: no
          keyboard activation, no focus ring, and nothing announced as interactive
          despite already carrying aria-haspopup/expanded, which promised a control
          that was not there.
          Button styling has to be reset explicitly (font/border/text-align/width),
          since the element previously inherited div defaults. */}
      <Box
        component="button"
        type="button"
        id="profile-dropdown"
        aria-controls={open ? 'user-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={accountHover.handleTriggerClick}
        // Hover-with-intent, additive to the click above: the pointer has to REST here,
        // so travelling up the rail past the chip does not open it. See `useHoverIntent`.
        {...accountHover.triggerProps}
        sx={{
          cursor: 'pointer',
          position: 'sticky',
          // The SIDEBAR's surface, not gray[50]. This block is sticky, so it has to be
          // opaque — content scrolls under it — but it was opaque in a different colour
          // from the rail it sits in, which is why it read as a separate grey panel
          // rather than part of the sidebar.
          background: sidebarSurface(theme),
          bottom: 0,
          p: 4,
          left: 0,
          minHeight: '76px',
          height: '76px',
          // width + border-box, NOT `minWidth: '100%'`. With `p: 4` and the default
          // content-box sizing, a 100% MIN-width made this button 295px inside a
          // 255px container: it overflowed and was simply clipped by the sidebar's
          // `overflow: hidden`. That is why the email never showed an ellipsis —
          // `noWrap` can only truncate against a BOUNDED width, and nothing here
          // was ever bounded (measured: email scrollWidth 207 === clientWidth 207,
          // i.e. not overflowing at all; the clipping happened four levels up).
          width: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
          justifyContent: 'center',
          // button reset
          font: 'inherit',
          color: 'inherit',
          border: 'none',
          textAlign: 'left',
          display: 'block',
          // The same neutral hover every other sidebar row uses (navChrome: hover is
          // deliberately neutral because it is transient, while a SELECTED row is
          // brand-tinted). Going through action.hover rather than a literal keeps this
          // row following that rule if it changes.
          transition: 'background-color .15s ease',
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
          '&:focus-visible': {
            outline: 'none',
            backgroundColor: theme.palette.action.hover,
            boxShadow: `inset 0 0 0 2px ${theme.palette.brand?.[500] || '#8F68EA'}`,
          },
        }}
      >
        {/* `width: 1` (100%) + `minWidth: 0`, not `minWidth: 1`. A 100% MIN-width
            forbids this row from shrinking, so the text column below could never
            give up space and the address overflowed instead of truncating.
            `minWidth: 0` is what lets a flex row shrink past its content. */}
        <Stack
          direction="row"
          justifyContent="left"
          pl={1}
          alignItems="center"
          spacing={3}
          sx={{ width: 1, minWidth: 0 }}
        >
          {!profile ? (
            <Skeleton variant="circular">
              <Avatar />
            </Skeleton>
          ) : (
            <Avatar
              src={sourceProfile?.avatar ?? undefined}
              alt={name}
              sx={{
                width: 40,
                height: 40,
                fontSize: 14,
                fontWeight: 600,
                border: `1px solid ${theme.palette.primary[50]}`,
                backgroundColor: theme.palette.primary[100],
                color: theme.palette.text.secondary,
              }}
            >
              {initials}
            </Avatar>
          )}
          {sidebarOpen && (
            // minWidth: 0 lets this column shrink inside the row flex so the
            // name/email truncate with an ellipsis instead of overflowing.
            <Stack direction="column" sx={{ minWidth: 0 }}>
              <Typography variant="body2" noWrap>{!profile ? <Skeleton width={120} /> : name}</Typography>
              <Typography variant="body4" color="text.secondary" noWrap>
                {!profile || planLoading ? <Skeleton width={140} /> : secondaryLine}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Box>
      {/* Padding matches every other menu in the app via MENU_PAPER_SX/MENU_ITEM_SX
          (@repo/ui) — was hand-copied magic numbers that drifted from ActionDropdown's. */}
      <Menu
        id="user-menu"
        action={menuPosition}
        anchorEl={anchorEl}
        // Collapsed, the chip is only 80px wide, so a centred menu would sit almost
        // entirely ON TOP of the sidebar it belongs to. Open it to the RIGHT instead,
        // bottom-aligned with the chip — the same placement Vergence uses for its
        // profile menu. The anchor Box is minWidth:100%, so its right edge IS the
        // drawer's right edge and ml:3 (12px) is a true gap, not a guess.
        // Expanded, the sidebar is 256px and the 220px menu fits comfortably within
        // it, so the existing centred placement is kept — it does not cover page
        // content and there is no reason to move a control the user already knows.
        transformOrigin={SIDEBAR_MENU_TRANSFORM_ORIGIN}
        anchorOrigin={SIDEBAR_MENU_ANCHOR_ORIGIN}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        // False for a mouse click (unchanged), true when the chip was activated from the
        // keyboard, which is the only way MUI's arrow handling on the MenuList ever
        // receives a key. A hover-opened menu must never take focus.
        autoFocus={accountHover.openedByKeyboard}
        // The Modal-level focus props, gated on HOW the menu opened — see the same note
        // in `ActionDropdown`. Suppressed for a pointer open (a menu that appeared
        // because the cursor drifted must not move the caret); left at MUI's defaults
        // for a keyboard open, which is the case that needs focus inside the list for
        // the arrow keys to work at all.
        //
        // `disableScrollLock` is not about focus and is unconditional: locking body
        // scroll — and paying its scrollbar compensation — every time the cursor rested
        // on the chip would shift the page under the user.
        // Only AUTO is gated on how the menu opened; ENFORCE and RESTORE are always
        // suppressed. See the note in `ActionDropdown` for what each one was measured to
        // do — the short version is that the FocusTrap fights the restore, and MUI's own
        // restore targets a node being unmounted and drops focus to <body>, so
        // `useHoverIntent` is the single actor that hands focus back to the trigger.
        disableAutoFocus={!accountHover.openedByKeyboard}
        disableEnforceFocus
        disableRestoreFocus
        disableScrollLock
        MenuListProps={{
          sx: {
            // Horizontal inset now lives on the paper (MENU_PAPER_SX below) — the
            // house convention shared by every menu in the app (VERA-891 originally
            // matched this to ActionDropdown's OLD inset; both now share the same
            // constant instead of copy-pasted magic numbers).
            pt: 0,
            pb: 0,
            minWidth: '220px',
          },
        }}
        slotProps={{
          // Note 3 in `useHoverIntent`: the modal root covers the chip the instant the
          // menu opens, ending its hover and closing the menu the user never left.
          root: { sx: HOVER_MENU_ROOT_SX },
          paper: {
            // The paper joins the chip's hover region, so the trip from one to the other
            // is not read as a dismissal.
            ...accountHover.surfaceProps,
            // Per-icon hover motion, applied at THIS menu's own Paper — see
            // `accountMenuIconMotionSx` in components/navigation/navIconMotion.ts for
            // the recipes, and for why this menu is a deliberate single exception rather
            // than the start of a sweep across every popover in the app.
            //
            // It goes here rather than into `menuPaperSx` above ON PURPOSE: that object
            // is also spread by the Theme SUBMENU, so putting the motion in it would
            // animate a surface this was never scoped to. The scope IS this Paper.
            sx: {
              ...menuPaperSx,
              ...SIDEBAR_MENU_PAPER_SX_FLUSH,
              ...accountMenuIconMotionSx,
              // ...and pointer events back ON for the paper, which does need to be
              // hovered and clicked.
              ...HOVER_MENU_PAPER_SX,
            },
          },
        }}
      >
        {/* replace icons with ours */}
        {/* Icon -> label gap is 3 (12px) in every row of this menu, matching the
            shared ActionDropdown. 1.5 (6px) read as the glyph crowding the word. */}
        <MenuItem
          onClick={() => {
            navigate(ROUTES.SETTINGS_PERSONAL_PROFILE)
          }}
          onMouseEnter={handleSiblingRowEnter}
          sx={{ ...MENU_ITEM_SX, minHeight: '44px' }}
        >
          <Stack direction="row" spacing={3} alignItems="center" sx={{ width: '100%' }}>
            {/* Single-user icon (matches the Users nav icon, no enclosing circle) so this
                row (personal) is visually distinct from the company "Settings" gear. It
                suits either label: the glyph says "you", which is what both "Profile" and
                "Account" name. Internal staff have no gear to be distinguished from, so
                nothing is lost there either. */}
            <User strokeWidth={1} />
            {/* Destination unchanged — the navigate above still targets
                SETTINGS_PERSONAL_PROFILE. This is a label change only. */}
            <Typography variant="body2">{accountRowLabel}</Typography>
          </Stack>
        </MenuItem>
        <MenuItem
          id="theme-menu-item"
          ref={themeRowRef}
          onClick={handleThemeClick}
          {...themeHover.triggerProps}
          onKeyDown={handleThemeRowKeyDown}
          aria-haspopup="menu"
          aria-expanded={themeOpen}
          aria-controls={themeOpen ? 'theme-submenu' : undefined}
          sx={{ ...MENU_ITEM_SX, minHeight: '44px' }}
        >
          <Stack direction="row" spacing={3} alignItems="center" sx={{ width: '100%' }}>
            {/* The SAME glyph as Settings → Personal → Appearance (`ContrastIcon`,
                `strokeWidth: 1`), because it is the same concept reached two ways. A
                paintbrush said "draw something", which this row does not do, and it
                left the app naming one idea with two marks. */}
            <Contrast strokeWidth={1} />
            <Typography variant="body2">Theme</Typography>
            <ChevronRight strokeWidth={1} style={{ position: 'absolute', right: 8 }} />
          </Stack>
        </MenuItem>
        <Divider flexItem />
        {/* NOT destructive, and not red.
            Logging out destroys nothing — it ends a session, and logging back in undoes
            it. §38's test is "does a row disappear?"; this fails it. Red here also
            contradicted the same action on Settings → Profile, which renders a plain
            secondary "Log out" button, so one action had two severities depending on where
            you found it. Spending the error colour on the most routine, most reversible
            control in the menu is the same dilution as spending the accent on four things
            at once: when everything is marked, nothing is. */}
        <MenuItem
          onClick={() => logout()}
          onMouseEnter={handleSiblingRowEnter}
          sx={{ ...MENU_ITEM_SX, mt: 1, minHeight: '44px' }}
        >
          <Stack direction="row" spacing={3} alignItems="center" sx={{ width: '100%' }}>
            <LogOut strokeWidth={1} />
            <Typography variant="body2">Log out</Typography>
          </Stack>
        </MenuItem>
      </Menu>
      <Menu
        id="theme-submenu"
        action={themeMenuPosition}
        anchorEl={themeAnchorEl}
        open={themeOpen}
        onClose={closeThemeMenuNow}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        /*
          FOCUS: hover-open must not move it, keyboard-open must.

          `autoFocus` is the MenuList's own — true only when the row was activated by
          Enter/Space/ArrowRight, so the keyboard user lands inside the submenu and the
          pointer user's caret stays where they left it.

          The three `disable*` props are Modal-level and always on, which is a different
          concern from the above. A nested Modal is the TOP modal, so by default it traps
          focus: MUI's FocusTrap re-checks on an interval and would drag focus out of the
          parent menu a beat after the pointer merely drifted over Theme. `disableAutoFocus`
          stops it taking focus on open, `disableEnforceFocus` stops it stealing focus back
          afterwards, and `disableRestoreFocus` keeps it from fighting the explicit
          `themeRowRef.current?.focus()` that ArrowLeft/Escape performs.
        */
        autoFocus={themeHover.openedByKeyboard}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        disableScrollLock
        MenuListProps={{
          'aria-labelledby': 'theme-menu-item',
          onKeyDown: handleThemeSubmenuKeyDown,
          sx: { minWidth: '200px', pt: 0, pb: 0 },
        }}
        slotProps={{
          /*
            The invisible modal backdrop is switched OFF for pointer purposes — see note 3
            in the docblock at the top of this file. Without it the backdrop covers the
            Theme row the instant the submenu opens, the row loses hover, and the submenu
            closes itself while the pointer has not moved.
          */
          root: { sx: HOVER_MENU_ROOT_SX },
          paper: {
            // ...and back ON for the paper, which does need to be hovered and clicked.
            // BOTH regions — see `submenuPointerProps`: this paper is a portal sibling of
            // the account menu, not a child, so the parent needs telling that the pointer
            // is still inside its menu system.
            ...submenuPointerProps,
            // Horizontal placement (and why it is the number it is) lives in
            // `sidebarMenuPlacement` — see `submenuPaperSx` above.
            //
            // The motion comes from its OWN named export rather than from the parent's
            // `accountMenuIconMotionSx` or from `menuPaperSx` — see the ⚠️ on that export
            // for why hoisting it would animate whichever surface spread the object next.
            sx: { ...submenuPaperSx, ...HOVER_MENU_PAPER_SX, ...themeSubmenuIconMotionSx },
          },
        }}
      >
        {([
          { value: 'light', label: 'Light', Icon: Sun },
          { value: 'dark', label: 'Dark', Icon: Moon },
          { value: 'system', label: 'System', Icon: Laptop },
        ] as const).map(({ value, label, Icon }) => (
          <MenuItem
            key={value}
            onClick={() => handleSelectTheme(value)}
            // Pick-one-of-three, so the rows are radios rather than plain commands. The
            // check glyph below already says this visually; `aria-checked` is the same
            // fact for a reader that cannot see it.
            role="menuitemradio"
            aria-checked={colorMode.mode === value}
            sx={{ ...MENU_ITEM_SX, minHeight: '44px' }}
          >
            <Stack direction="row" spacing={3} alignItems="center" sx={{ width: '100%' }}>
              <Icon strokeWidth={1} size={20} />
              <Typography variant="body2" sx={{ flexGrow: 1 }}>
                {label}
              </Typography>
              {colorMode.mode === value && <Check strokeWidth={2} size={18} />}
            </Stack>
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

{
  /* <RadioGroup
aria-labelledby="demo-theme-toggle"
name="theme-toggle"
row
value={mode}
onChange={(event) =>
  setMode(event.target.value as 'system' | 'light' | 'dark')
}
>
<FormControlLabel value="system" control={<Radio />} label="System" />
<FormControlLabel value="light" control={<Radio />} label="Light" />
<FormControlLabel value="dark" control={<Radio />} label="Dark" />
</RadioGroup> */
}

// <Menu
// id="theme-menu"
// anchorEl={anchorEl}
// open={open}
// onClose={handleClose}
// onClick={handleClose}
// autoFocus={false}
// MenuListProps={{
//   sx: {
//     minWidth: '200px',
//     padding: '4px 0',
//   },
// }}
// slotProps={{
//   paper: {
//     sx: {
//       mt: '8px',
//       borderRadius: '8px',
//       boxShadow: `rgba(145, 158, 171, 0.12) 0px 12px 24px -4px, rgba(145, 158, 171, 0.2) 0px 0px 2px 0px`,
//     },
//   },
// }}
// transformOrigin={{ horizontal: 'right', vertical: 'top' }}
// anchorOrigin={{ horizontal: 'right', vertical: 'bottom'}}
// >

// </Menu>
